import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";
import { DataWithPagination } from "services/api/types";
import { Pagination } from "services/search";

import { FleetDocumentUpload } from "../documents/types";
import {
  transformFleetVehicleService,
  transformFleetVehicleServiceOnSave,
  transformVehicleServiceStatus,
} from "./transformations";
import {
  FleetVehicleService,
  FleetVehicleServiceEditFormValues,
  FleetVehicleServiceRow,
  FleetVehicleServiceSearchFormValues,
  VehicleServiceStatus,
} from "./types";

/*---------------------------------------------------------------------------------------
  STATUSI
-----------------------------------------------------------------------------------------*/
export const getVehicleServiceStatusesAPI = async (): Promise<
  VehicleServiceStatus[]
> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/services/statuses`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data.map(transformVehicleServiceStatus);
};

/*---------------------------------------------------------------------------------------
  PRETRAGA
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleServicesAPI = async (
  pagination: Pagination,
  filters: FleetVehicleServiceSearchFormValues
): Promise<DataWithPagination<FleetVehicleServiceRow>> => {
  let path = `/v1/fleet/vehicles/services`;

  // Bez `vehicleId` endpoint vraća naloge cijelog tenanta — to koristi zajednički pregled.
  const params: string[] = [];
  if (filters.vehicleId) params.push(`vehicleId=${filters.vehicleId}`);
  if (filters.vehicleServiceStatusId)
    params.push(`vehicleServiceStatusId=${filters.vehicleServiceStatusId}`);
  if (filters.search) params.push(`name=${encodeURIComponent(filters.search)}`);
  if (filters.contactId) params.push(`contactId=${filters.contactId}`);
  if (filters.vehicleManufacturerId)
    params.push(`vehicleManufacturerId=${filters.vehicleManufacturerId}`);
  if (filters.vehicleModelId) params.push(`vehicleModelId=${filters.vehicleModelId}`);
  if (filters.vehicleTypeId) params.push(`vehicleTypeId=${filters.vehicleTypeId}`);
  if (filters.receivedDateFrom)
    params.push(`receivedDateFrom=${filters.receivedDateFrom.toISOString()}`);
  if (filters.receivedDateTo)
    params.push(`receivedDateTo=${filters.receivedDateTo.toISOString()}`);
  if (filters.isOpen !== null && filters.isOpen !== undefined)
    params.push(`isOpen=${filters.isOpen}`);
  if (params.length > 0) path += `?${params.join("&")}`;

  return await paginatedApiCall(path, pagination, transformFleetVehicleService);
};

/*---------------------------------------------------------------------------------------
  DETALJ
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleServiceAPI = async (
  id: string
): Promise<FleetVehicleService> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/services/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleService(response.data);
};

/*---------------------------------------------------------------------------------------
  UNOS
-----------------------------------------------------------------------------------------*/
/**
 * Otvaranje naloga bez promjene statusa — naknadni unos servisa koji je već obavljen.
 * Nalog uz odlazak na servis ide kroz `updateFleetVehicleStatusAPI`, da nastane u istoj
 * transakciji kao i status.
 */
export const createFleetVehicleServiceAPI = async (
  vehicleId: string,
  formValues: FleetVehicleServiceEditFormValues,
  documentUploads: FleetDocumentUpload[],
  t: (key: string) => string
): Promise<FleetVehicleService> => {
  const response = await createApiCall(
    {
      path: "/v1/fleet/vehicles/services",
      method: "POST",
      body: {
        vehicleId,
        ...transformFleetVehicleServiceOnSave(formValues, documentUploads),
      },
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.service.create.message.success"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleService(response.data);
};

/*---------------------------------------------------------------------------------------
  IZMJENA
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleServiceAPI = async (
  serviceId: string,
  formValues: FleetVehicleServiceEditFormValues,
  documentUploads: FleetDocumentUpload[],
  t: (key: string) => string
): Promise<FleetVehicleService> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/services/${serviceId}`,
      method: "PUT",
      body: transformFleetVehicleServiceOnSave(formValues, documentUploads),
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.service.update.message.success"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformFleetVehicleService(response.data);
};

/*---------------------------------------------------------------------------------------
  BRISANJE
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleServiceAPI = async (
  id: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/services/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.service.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return !!response.data?.id;
};
